/*
 * Copyright © 2021 Firecrest Clinical, Ltd.
 * All rights reserved.
*/

import {MarkType, Node as ProseMirrorNode} from 'prosemirror-model';
import {EditorState, Transaction} from 'prosemirror-state';
import {Command} from 'prosemirror-commands';
import {getSelectionNodes} from '../../../helpers';
import {applyMark, removeMark} from '../../../commands';
import {Dispatch} from './types';

type Name = 'text_size';
type AttrName = 'fontSize';

interface SizeAttrs {
    fontSize: string;
}

class TextSize {
    /**
     * Text size;.
     */
    textSize: string = '14px';
    
    /**
     * Name.
     */
    name: Name;

    /**
     * Attribute Name.
     */
    attrName: AttrName;

    /**
     * Default constructor.
     *
     * @param textSize Text size.
     */
    constructor(textSize: string) {
        this.name = 'text_size';
        this.attrName = 'fontSize';
        this.textSize = textSize;
    }

    /**
     * Apply command.
     * 
     * @param attrs Color Attribute.
     * @returns Command.
     */
    apply(attrs: SizeAttrs): Command {
        return (state: EditorState, dispatch?: Dispatch): boolean => {
            const {schema, selection, doc} = state;

            const type: MarkType = schema.marks[this.name];
            if (!type) {
                return false;
            }

            const {from, to, empty} = selection;

            if (!empty && (from + 1 === to)) {
                const node = doc.nodeAt(from);
                if (node?.isAtom && !node.isText && node.isLeaf) {
                    // An atomic node (e.g. Image) is selected.
                    return false;
                }
            }

            return applyMark(type, {...attrs, fontSize: this.textSize})(state, dispatch);
        };
    }

    /**
     * Toggle heading.
     *
     * @returns Command.
     */
    toggle(): Command {
        return (state: EditorState, dispatch?: (tr: Transaction) => void): boolean => {
            return this.apply({fontSize: this.textSize})(state, dispatch);
        };
    }
    
    /**
     * Is command active.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    isActive(state: EditorState): boolean {
        const {schema} = state;
        const nodesInSelection = getSelectionNodes(state);
        const type: MarkType = schema.marks[this.name];

        if (!type) {
            return false;
        }

        const activeNode = nodesInSelection.find((node: ProseMirrorNode) => {
            if (node.marks.length) {
                for (let i = 0; i < node.marks.length; i++) {
                    if (node.marks.length && node.marks[i].attrs && node.marks[i].attrs.fontSize === this.textSize) {
                        return true;
                    }
                }
                return false;    
            }
            return false;
        });

        return Boolean(activeNode);
    }

    /**
     * Remove command.
     * 
     * @returns Command.
     */
    remove(): Command {
        return (state: EditorState, dispatch?: Dispatch): boolean => {
            const {schema} = state;

            const type = schema.marks[this.name];
            if (!type) {
                return false;
            }

            return removeMark(type)(state, dispatch);
        };
    }

    /**
     * Does command can execute.
     * 
     * @param state EditorState.
     * @returns Boolean.
     */
    canExecute(state: EditorState): boolean {
        const attrs = {fontSize: '14px'};
        return this.apply(attrs)(state);
    }
}

export default TextSize;
